import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  InputAdornment,
  Divider,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { UnifiedTableData } from '../../types/unifiedSchema';

interface TableSidebarProps {
  tables: UnifiedTableData[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

const SIDEBAR_WIDTH = 260;

const TableSidebar: React.FC<TableSidebarProps> = ({ tables, selectedIndex, onSelect }) => {
  const [filter, setFilter] = useState('');

  const filteredTables = useMemo(() => {
    if (!filter.trim()) return tables.map((t, i) => ({ table: t, originalIndex: i }));
    const q = filter.toLowerCase().trim();
    return tables
      .map((t, i) => ({ table: t, originalIndex: i }))
      .filter(
        ({ table }) =>
          table.file_name.toLowerCase().includes(q) ||
          table.semantic_name.toLowerCase().includes(q) ||
          table.entity_type.toLowerCase().includes(q) ||
          table.columns.some(
            (c) =>
              c.name.toLowerCase().includes(q) ||
              (c.original_name || '').toLowerCase().includes(q)
          )
      );
  }, [tables, filter]);

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Search */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter tables..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Divider />

      {/* Table list */}
      <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {filteredTables.map(({ table, originalIndex }) => (
          <ListItemButton
            key={originalIndex}
            selected={selectedIndex === originalIndex}
            onClick={() => onSelect(originalIndex)}
            sx={{
              py: 1,
              px: 1.5,
              borderLeft: selectedIndex === originalIndex ? 3 : 0,
              borderColor: 'primary.main',
            }}
          >
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  fontWeight={selectedIndex === originalIndex ? 'bold' : 'normal'}
                  noWrap
                >
                  {table.semantic_name || table.file_name}
                </Typography>
              }
              secondary={
                <Box
                  component="span"
                  sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}
                >
                  <Chip
                    label={table.entity_type}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Chip
                    label={`${table.columns.length} cols`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  {table.row_count > 0 && (
                    <Chip
                      label={`${table.row_count.toLocaleString()} rows`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItemButton>
        ))}
        {filteredTables.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tables match filter
            </Typography>
          </Box>
        )}
      </List>

      {/* Footer */}
      <Divider />
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {tables.length} table{tables.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default TableSidebar;
export { SIDEBAR_WIDTH };
